import { PlusOutlined } from '@ant-design/icons';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import {
  BaseEdge, EdgeLabelRenderer, type EdgeProps, getSmoothStepPath,
  type Node,
} from '@xyflow/react';
import { NodeType } from 'automations/data/nodeTypes';
import { useAppSelector } from 'automations/hooks/useAppSelector';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import { automationStatusSelector } from 'automations/selectors/automationConfig';
import { allowAnimationSelector, hoveredEdgeIdSelector, nodeSelector } from 'automations/selectors/reactFlow';
import { addNodeBetweenExistingNodes } from 'automations/store/reactFlowSlice';
import { ConditionTypes } from 'automations/types/automationConfig';
import { type CommonNodeData } from 'automations/types/nodeSchemas';
import { defaultEdgeLabelStyle } from 'automations/utils/customEdgeUtil';
import { generateNewNodePayload } from 'automations/utils/node';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import styles from './CustomEdgeBase.module.scss';

// Define the marker ID
const MARKER_ID = 'custom-arrow';

export default function CustomEdgeBase({
  edgeProps,
  centerContent,
  hideAddNode = false,
}: {
  edgeProps: EdgeProps;
  centerContent?: React.ReactNode;
  hideAddNode?: boolean;
}) {
  const {
    id, sourceX, sourceY, targetX, targetY,
    source: sourceId, target: targetId,
  } = edgeProps;

  const sourceNode = useSelector(nodeSelector(sourceId));
  const targetNode = useSelector(nodeSelector(targetId)) as Node<CommonNodeData>;
  const automationStatus = useSelector(automationStatusSelector);
  const dispatch = useAutomationDispatch();
  const hoveredEdgeId = useAppSelector(hoveredEdgeIdSelector);
  const isHovered = hoveredEdgeId === id;

  const targetExecStatus = targetNode?.data?.executionStatus;

  const allowAnimation = useAppSelector(allowAnimationSelector(targetId));

  const isAnimated = Boolean(
    allowAnimation && targetExecStatus && (targetExecStatus !== AutomationExecutionNodeStatusEnum.PENDING),
  );
  const isSuccess = isAnimated && targetExecStatus === AutomationExecutionNodeStatusEnum.SUCCESS;
  const isError = isAnimated && targetExecStatus === AutomationExecutionNodeStatusEnum.FAILED;

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX, sourceY, targetX, targetY,
  });

  const handleAddNode = useCallback(() => {
    dispatch(addNodeBetweenExistingNodes({
      sourceId,
      targetId,
      newNode: generateNewNodePayload({
        nodeType: NodeType.newBlock,
        data: {
          executionOrder: 1,
        },
      }),
    }));
  }, [dispatch, sourceId, targetId]);

  const AddNodeContent = useMemo(() => {
    if (
      sourceNode?.type === NodeType.newBlock
      || targetNode?.type === NodeType.newBlock
      || sourceNode?.data?.nodeSubType === ConditionTypes.multiIfElse
      || automationStatus !== AutomationStatus.draft
      || hideAddNode
    ) {
      return null;
    } else {
      return (
        <div
          className={styles.customHandle}
          onClick={handleAddNode}
          role="button"
          tabIndex={0}
          aria-label="Add node"
          onKeyDown={handleAddNode}
        >
          <PlusOutlined className={styles.handleIcon} />
        </div>
      );
    }
  }, [automationStatus, handleAddNode, hideAddNode, sourceNode?.data?.nodeSubType, sourceNode?.type, targetNode?.type]);

  return (
    <>
      <defs>
        <marker
          id={`${MARKER_ID}-${id}`}
          viewBox="0 0 25 25"
          refX="12.6391"
          refY="13.5"
          markerWidth="25"
          markerHeight="25"
          orient=""
        >
          <path
            // eslint-disable-next-line max-len
            d="M20.6697 10.1296L13.1697 17.6296C13.1001 17.6993 13.0173 17.7546 12.9263 17.7923C12.8352 17.8301 12.7377 17.8495 12.6391 17.8495C12.5405 17.8495 12.4429 17.8301 12.3519 17.7923C12.2608 17.7546 12.1781 17.6993 12.1085 17.6296L4.60846 10.1296C4.46773 9.98882 4.38867 9.79795 4.38867 9.59893C4.38867 9.3999 4.46773 9.20903 4.60846 9.0683C4.74919 8.92757 4.94007 8.84851 5.13909 8.84851C5.33811 8.84851 5.52898 8.92757 5.66971 9.0683L12.6391 16.0386L19.6085 9.0683C19.6781 8.99862 19.7609 8.94334 19.8519 8.90563C19.943 8.86792 20.0405 8.84851 20.1391 8.84851C20.2376 8.84851 20.3352 8.86792 20.4263 8.90563C20.5173 8.94334 20.6 8.99862 20.6697 9.0683C20.7394 9.13799 20.7947 9.22071 20.8324 9.31176C20.8701 9.4028 20.8895 9.50038 20.8895 9.59893C20.8895 9.69747 20.8701 9.79506 20.8324 9.8861C20.7947 9.97714 20.7394 10.0599 20.6697 10.1296Z"
            className={clsx(
              styles.path,
              (isAnimated && isSuccess) && styles.successPath,
              (isAnimated && isError) && styles.errorPath,
            )}
          />
        </marker>
      </defs>
      <BaseEdge
        id={id}
        path={path}
        className={clsx(
          styles.edgePath,
          isAnimated && styles.animated,
          isHovered && styles.hovered,
          (isAnimated && isSuccess) && styles.success,
          (isAnimated && isError) && styles.error,
        )}
        markerEnd={`url(#${MARKER_ID}-${id})`}
      />
      <EdgeLabelRenderer>
        <div style={defaultEdgeLabelStyle({ labelX, labelY })}>
          {(isHovered && AddNodeContent) ? AddNodeContent : centerContent}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
